import type { PaymentActions } from '@/scopes/payment/domain/actions'
import * as React from 'react'
import { use } from 'react'
import { PaymentScopePage } from '@/scopes/payment/domain/types'

export interface PaymentContext {
  contextItemId: string
  groupUserId: string | null
  scope: PaymentScopePage
  bookingNumber: string
}

export type PaymentStep = 'amounts' | 'methods'

interface PaymentStepContextValue {
  isWizard: boolean
  step: PaymentStep
  goToAmounts: () => void
  goToMethods: () => void
}

export const PaymentReactContext = React.createContext<PaymentContext | null>(null)
const PaymentStepReactContext = React.createContext<PaymentStepContextValue | null>(null)
const PaymentActionsReactContext = React.createContext<PaymentActions | null>(null)

export function usePaymentContext() {
  const context = use(PaymentReactContext)
  if (!context) {
    throw new Error(
      'Attempted to access payment context before initializing it, please use PaymentReactContext to initialize the context.',
    )
  }

  return context
}

interface PaymentStepProviderProps {
  isWizard: boolean
  children: React.ReactNode
}

export function PaymentStepProvider({ isWizard, children }: PaymentStepProviderProps) {
  const [step, setStep] = React.useState<PaymentStep>('amounts')
  const goToAmounts = React.useCallback(() => setStep('amounts'), [])
  const goToMethods = React.useCallback(() => setStep('methods'), [])

  React.useEffect(() => {
    if (!isWizard && step !== 'amounts') {
      setStep('amounts')
    }
  }, [isWizard, step])

  const value = React.useMemo<PaymentStepContextValue>(
    () => ({
      isWizard,
      step,
      goToAmounts,
      goToMethods,
    }),
    [goToAmounts, goToMethods, isWizard, step],
  )

  return <PaymentStepReactContext value={value}>{children}</PaymentStepReactContext>
}

export function usePaymentStep() {
  const context = React.use(PaymentStepReactContext)

  if (!context) {
    throw new Error('usePaymentStep must be used within a PaymentStepProvider')
  }

  return context
}

interface PaymentActionsProviderProps {
  actions: PaymentActions
  children: React.ReactNode
}

export function PaymentActionsProvider({ actions, children }: PaymentActionsProviderProps) {
  return <PaymentActionsReactContext value={actions}>{children}</PaymentActionsReactContext>
}

export function usePaymentActions() {
  const context = React.use(PaymentActionsReactContext)

  if (!context) {
    throw new Error('usePaymentActions must be used within a PaymentActionsProvider')
  }

  return context
}
